import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import { Input } from "@shadcn/components/ui/input";
import { Button } from "@shadcn/components/ui/button";
import { Loader2, MapPin } from "lucide-react";
import { addressApi, AddressAutocompleteResult, AddressLookupResult } from "./api";
import { AddressLookupProps } from "./AddressLookup.types";
import { getCountrySelectOptions } from "./getCountrySelectOptions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shadcn/components/ui/select";

export const AddressLookup = ({ name, required, disabled, countries, helperText, addressFieldsRequired = true }: AddressLookupProps) => {
  const description = getFieldHelperText(helperText, required);
  const { control, setValue, watch, getValues, formState } = useFormContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<AddressAutocompleteResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressLookupResult | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasBeenClearedRef = useRef(false);
  const sessionTokenRef = useRef<string | null>(null);

  const debounceDelay = 600;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const countryOptions = useMemo(() => getCountrySelectOptions(countries), [countries]);

  // Request user's location when search input gets focus
  const requestUserLocation = useCallback(() => {
    // Only request once per component lifecycle
    if (locationRequested || userLocation) {
      return;
    }

    if (!navigator.geolocation) {
      return;
    }

    setLocationRequested(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        setLocationRequested(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 0, // Force fresh request to ensure permission prompt appears
      }
    );
  }, [locationRequested, userLocation]);

  const performSearch = useCallback(
    async (query: string) => {
      if (!query || query.length < 3) {
        setResults([]);
        setShowDropdown(false);
        return;
      }

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = generateUUID();
      }

      setIsSearching(true);
      setShowDropdown(true);

      try {
        const response = await addressApi.search({
          query,
          countries: countries && countries.length > 0 ? countries : undefined,
          location: userLocation || undefined,
          sessionToken: sessionTokenRef.current ?? undefined,
        });
        setResults(response.results || []);
        setHighlightedIndex(null);
      } catch (error) {
        console.error("Address search error:", error);
        setResults([]);
        setHighlightedIndex(null);
      } finally {
        setIsSearching(false);
      }
    },
    [countries, userLocation]
  );

  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      if (searchQuery) {
        performSearch(searchQuery);
      } else {
        setResults([]);
        setShowDropdown(false);
      }
    }, debounceDelay);

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [searchQuery, performSearch]);

  useEffect(() => {
    if (!showDropdown) {
      setHighlightedIndex(null);
    }
  }, [showDropdown]);

  const handleSelectAddress = useCallback(
    async (autocompleteResult: AddressAutocompleteResult) => {
      setIsFetchingDetails(true);
      setShowDropdown(false);
      setHighlightedIndex(null);
      setSearchQuery(autocompleteResult.text);
      
      try {
        const token = sessionTokenRef.current ?? undefined;
        const detailsResponse = await addressApi.getDetails({
          placeId: autocompleteResult.placeId,
          sessionToken: token,
        });
        sessionTokenRef.current = null;
        const address = detailsResponse.result;
        
        setSelectedAddress(address);
        setSearchQuery("");
        setManualMode(false);
        hasBeenClearedRef.current = false;

        const addressLine1 = [address.streetNumber, address.route].filter(Boolean).join(" ").trim() || "";
        const addressLine2 = [address.premise, address.subpremise].filter(Boolean).join(" ").trim() || "";

        setValue(`${name}.addressLine1`, addressLine1);
        setValue(`${name}.addressLine2`, addressLine2);
        setValue(`${name}.postalCode`, address.postalCode || "");
        setValue(`${name}.town`, address.postalTown || "");
        setValue(`${name}.adminArea1`, address.adminArea1 || "");
        setValue(`${name}.adminArea2`, address.adminArea2 || "");
        setValue(`${name}.country`, address.country || "");
        setValue(`${name}.countryIso2`, address.countryIso2 || "");
      } catch (error) {
        console.error("Failed to fetch address details:", error);
        setShowDropdown(true);
        setSearchQuery("");
      } finally {
        setIsFetchingDetails(false);
      }
    },
    [name, setValue]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown || results.length === 0 || isSearching || isFetchingDetails) {
        if (e.key === "Escape") {
          setShowDropdown(false);
          setHighlightedIndex(null);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) => {
            if (prev === null || prev === results.length - 1) {
              return 0;
            }
            return prev + 1;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => {
            if (prev === null || prev === 0) {
              return results.length - 1;
            }
            return prev - 1;
          });
          break;
        case "Enter":
          e.preventDefault();
          if (highlightedIndex !== null && results[highlightedIndex]) {
            handleSelectAddress(results[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setShowDropdown(false);
          setHighlightedIndex(null);
          break;
      }
    },
    [showDropdown, results, isSearching, isFetchingDetails, highlightedIndex, handleSelectAddress]
  );

  useEffect(() => {
    if (highlightedIndex !== null && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`
      ) as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setHighlightedIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleManualMode = () => {
    setManualMode(true);
    setShowDropdown(false);
    setSearchQuery("");
    setSelectedAddress(null);
    hasBeenClearedRef.current = false;

    // Clear form fields
    setValue(`${name}.addressLine1`, "");
    setValue(`${name}.addressLine2`, "");
    setValue(`${name}.postalCode`, "");
    setValue(`${name}.town`, "");
    setValue(`${name}.adminArea1`, "");
    setValue(`${name}.adminArea2`, "");
    setValue(`${name}.country`, "");
    setValue(`${name}.countryIso2`, "");
  };

  const handleSearchAgain = () => {
    sessionTokenRef.current = null;
    setManualMode(false);
    setSelectedAddress(null);
    setSearchQuery("");
    hasBeenClearedRef.current = true;
    
    // Set values to undefined to properly clear without falling back to defaults
    setValue(`${name}.addressLine1`, undefined as any, { shouldDirty: false, shouldValidate: false });
    setValue(`${name}.addressLine2`, undefined as any, { shouldDirty: false, shouldValidate: false });
    setValue(`${name}.postalCode`, undefined as any, { shouldDirty: false, shouldValidate: false });
    setValue(`${name}.town`, undefined as any, { shouldDirty: false, shouldValidate: false });
    setValue(`${name}.adminArea1`, undefined as any, { shouldDirty: false, shouldValidate: false });
    setValue(`${name}.adminArea2`, undefined as any, { shouldDirty: false, shouldValidate: false });
    setValue(`${name}.country`, undefined as any, { shouldDirty: false, shouldValidate: false });
    setValue(`${name}.countryIso2`, undefined as any, { shouldDirty: false, shouldValidate: false });
  };

  // Check for existing address data - prioritize current values, only use defaultValues if current is undefined
  const watchedAddressLine1 = watch(`${name}.addressLine1`);
  const currentAddressLine1 = getValues(`${name}.addressLine1`);
  const defaultAddressLine1 = formState.defaultValues?.[name]?.addressLine1;
  // Only use defaultValues if current value is undefined (not set yet), not if it's been cleared
  // If hasBeenClearedRef is true, don't fall back to defaults
  const addressLine1 = currentAddressLine1 !== undefined 
    ? currentAddressLine1 
    : (hasBeenClearedRef.current 
      ? undefined  // If we've explicitly cleared, don't use defaults
      : (defaultAddressLine1 ?? watchedAddressLine1));
  const hasExistingAddress = addressLine1 && typeof addressLine1 === 'string' && addressLine1.trim() !== "";
  
  useEffect(() => {
    if (hasExistingAddress && !manualMode && selectedAddress === null) {
      setManualMode(true);
    }
  }, [hasExistingAddress, manualMode, selectedAddress, addressLine1]);

  const showAddressFields = (manualMode || selectedAddress !== null || hasExistingAddress) && !isFetchingDetails;

  return (
    <div className="space-y-4">
      {!showAddressFields && (
        <div ref={containerRef} className="relative">
          <FormField
            control={control}
            name={`${name}.addressSearch`}
            rules={
              required
                ? {
                    validate: () => {
                      if (!showAddressFields) {
                        return "Please search for an address";
                      }
                      return true;
                    },
                  }
                : undefined
            }
            render={() => (
              <FormItem>
                {description && <FormDescription>{description}</FormDescription>}
                <FormControl>
                  <div className="relative">
                    <Input
                      id="address-search"
                      type="text"
                      placeholder="Start typing address..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setHighlightedIndex(null);
                        if (e.target.value.length >= 3) {
                          setShowDropdown(true);
                        }
                      }}
                      onFocus={() => {
                        console.debug("Search input focused, requesting location...");
                        requestUserLocation();
                        if (searchQuery.length >= 3 && results.length > 0) {
                          setShowDropdown(true);
                        }
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={disabled}
                      className="pr-10"
                    />
                    {(isSearching || isFetchingDetails) && (
                      <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                    )}
                    {!isSearching && !isFetchingDetails && searchQuery && (
                      <MapPin className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showDropdown && (
            <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
              <div ref={dropdownRef} className="max-h-[300px] overflow-auto p-1">
                {isSearching && (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                {!isSearching && searchQuery.length < 3 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Type at least 3 characters to search...
                  </div>
                )}
                {!isSearching && searchQuery.length >= 3 && (
                  <>
                    {results.length === 0 && (
                      <div className="py-3 text-center text-sm text-muted-foreground">No addresses found.</div>
                    )}
                    {results.length > 0 && (
                      <>
                        {results.map((result, index) => (
                          <button
                            key={index}
                            data-index={index}
                            type="button"
                            onClick={() => handleSelectAddress(result)}
                            disabled={isFetchingDetails}
                            className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed ${
                              highlightedIndex === index ? "bg-accent text-accent-foreground" : ""
                            }`}
                          >
                            <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="truncate text-left">{result.text}</span>
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          
          <div className="mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleManualMode}
              disabled={disabled}
            >
              Enter manually
            </Button>
          </div>
        </div>
      )}

      {showAddressFields && (
        <>
          <div className="flex items-center justify-end mb-4">
            <Button type="button" variant="outline" size="sm" onClick={handleSearchAgain} disabled={disabled}>
              Search again
            </Button>
          </div>
          <FormField
            control={control}
            name={`${name}.addressLine1`}
            rules={{
              required: addressFieldsRequired ? "Address Line 1 is required" : false,
            }}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Address Line 1
                </label>
                <FormControl>
                  <Input {...field} disabled={disabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={`${name}.addressLine2`}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Address Line 2
                </label>
                <FormControl>
                  <Input {...field} disabled={disabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={control}
              name={`${name}.town`}
              rules={{
                required: addressFieldsRequired ? "Town/City is required" : false,
              }}
              render={({ field }) => (
                <FormItem>
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Town/City
                  </label>
                  <FormControl>
                    <Input {...field} disabled={disabled} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name={`${name}.postalCode`}
              rules={{
                required: addressFieldsRequired ? "Postcode is required" : false,
              }}
              render={({ field }) => (
                <FormItem>
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Postcode
                  </label>
                  <FormControl>
                    <Input {...field} disabled={disabled} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={control}
            name={`${name}.countryIso2`}
            rules={{
              required: addressFieldsRequired && required ? "Country is required" : false,
            }}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Country
                </label>
                <Select
                  value={field.value || undefined}
                  onValueChange={(iso2) => {
                    field.onChange(iso2);
                    const opt = countryOptions.find((o) => o.iso2 === iso2);
                    setValue(`${name}.country`, opt?.name ?? "", {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  disabled={disabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a country" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {countryOptions.map((o) => (
                      <SelectItem key={o.iso2} value={o.iso2}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  );
};

function generateUUID(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }